import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Assessment, HDType, HD_TYPES } from '@/lib/types';
import { EnergyTrends } from './analytics/EnergyTrends';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ENERGY_CENTERS_BY_TYPE } from '@/lib/energyCenters';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  LineChart, 
  History, 
  PlusCircle,
  Sparkles,
  Star, 
  Zap,
  Brain,
  MessageCircle,
  User,
  Battery,
  Activity,
  Shield,
  Heart
} from 'lucide-react';
import { motion } from 'framer-motion';

interface DashboardPageProps {
  onNavigate: (page: 'welcome' | 'diagnosis' | 'history' | 'results') => void;
  userName?: string;
  userType?: HDType;
  assessments: Assessment[];
  photoUrl?: string;
}

const getMoodEmoji = (zone: 'green' | 'orange' | 'red') => {
  switch (zone) {
    case 'green': return '😊';
    case 'orange': return '😐';
    case 'red': return '😔';
    default: return '🤔';
  }
};

export function DashboardPage({ onNavigate, userName, userType, assessments, photoUrl }: DashboardPageProps) {
  const lastAssessment = assessments[0];
  const totalAssessments = assessments.length;
  const greenZones = assessments.filter(a => a.zone === 'green').length;
  const streakCount = assessments.filter((_, index) => index < 3).filter(a => a.zone === 'green').length;
  const availableCenters = userType ? ENERGY_CENTERS_BY_TYPE[userType] : [];
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Section 1: Personal Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 z-0">
              <div className="absolute top-0 left-0 w-72 h-72 bg-purple-500/10 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
              <div className="absolute top-0 right-0 w-72 h-72 bg-pink-500/10 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-72 h-72 bg-yellow-500/10 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 p-8">
              <div className="flex flex-col items-center text-center space-y-6">
                <motion.div 
                  className="relative"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={userName || 'Profile'}
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center border-4 border-white shadow-lg">
                      <User className="w-12 h-12 text-purple-600" />
                    </div>
                  )}
                  {userType && HD_TYPES[userType] && (
                    <motion.span 
                      className="absolute -bottom-2 -right-2 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md text-lg"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      {HD_TYPES[userType].emoji}
                    </motion.span>
                  )}
                </motion.div>

                <div className="space-y-3">
                  <motion.h1 
                    className="text-2xl font-bold bg-gradient-to-r from-[#54075B] to-pink-600 text-transparent bg-clip-text"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {language === 'fr' ? 'Bienvenue' : 'Welcome'} {userName} ✨
                  </motion.h1>

                  {userType && HD_TYPES[userType] && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-center gap-2 text-[#54075B]">
                        <span>{HD_TYPES[userType].name}</span>
                      </div>
                      <p className="text-gray-600 max-w-2xl mx-auto text-sm">
                        {HD_TYPES[userType].welcomeMessage[language]}
                      </p>
                    </motion.div>
                  )}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    onClick={() => onNavigate('diagnosis')}
                    className="bg-[#54075B] hover:bg-[#54075B]/90 gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <PlusCircle className="w-4 h-4" />
                    {language === 'fr' ? 'Nouveau diagnostic' : 'New Assessment'}
                  </Button>
                </motion.div>
              </div>

              {lastAssessment && (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="p-4 rounded-xl bg-white/50 backdrop-blur-sm hover:bg-white/70 transition-all duration-300">
                    <div className="flex items-center gap-2 text-purple-600">
                      <Activity className="w-5 h-5" />
                      <span className="font-semibold">Dernier diagnostic</span>
                    </div>
                    <span className="text-sm text-purple-600 block mt-1">
                      {format(new Date(lastAssessment.date), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                    </span>
                  </div>

                  <div className="p-4 rounded-xl bg-white/50 backdrop-blur-sm hover:bg-white/70 transition-all duration-300">
                    <div className="flex items-center gap-2">
                      <Battery className="w-5 h-5" />
                      <span className="font-semibold">État énergétique</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl">{getMoodEmoji(lastAssessment.zone)}</span>
                      <span className="text-sm capitalize">{lastAssessment.zone}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-white/50 backdrop-blur-sm hover:bg-white/70 transition-all duration-300">
                    <div className="flex items-center gap-2 text-purple-600">
                      <Shield className="w-5 h-5" />
                      <span className="font-semibold">Symptômes identifiés</span>
                    </div>
                    <span className="text-sm text-purple-600 block mt-1">
                      {(lastAssessment.symptoms || []).length} symptôme(s)
                    </span>
                  </div>
                </motion.div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Section 2: Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
              <div className="text-center space-y-4">
                <div className="p-4 rounded-full bg-purple-100 w-16 h-16 mx-auto flex items-center justify-center">
                  <Star className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">{totalAssessments}</div>
                  <div className="text-sm text-purple-500">Évaluations totales</div>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
              <div className="text-center space-y-4">
                <div className="p-4 rounded-full bg-green-100 w-16 h-16 mx-auto flex items-center justify-center">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {Math.round((greenZones / totalAssessments) * 100) || 0}%
                  </div>
                  <div className="text-sm text-green-500">Alignement global</div>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
              <div className="text-center space-y-4">
                <div className="p-4 rounded-full bg-yellow-100 w-16 h-16 mx-auto flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{streakCount}</div>
                  <div className="text-sm text-yellow-500">Séquence positive</div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Section 3: Latest Assessment Details */}
        {lastAssessment && lastAssessment.symptoms && lastAssessment.symptoms.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <Card className="p-6 bg-white/80 backdrop-blur-sm">
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-purple-600" />
                  Dernier diagnostic
                </h2>
                <div className="space-y-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Symptômes identifiés :</h3>
                    <div className="flex flex-wrap gap-2">
                      {lastAssessment.symptoms.map((symptom, index) => (
                        <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 rounded-md text-sm">
                          {symptom}
                        </span>
                      ))}
                    </div>
                  </div>
                  {lastAssessment.response && (
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Réponse :</h3>
                      <p className="text-purple-800">{lastAssessment.response}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Section 4: Charts and Trends */}
        {assessments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <Card className="p-6 bg-white/80 backdrop-blur-sm">
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-purple-600" />
                  Tendances énergétiques
                </h2>
                <p className="text-sm text-purple-600">
                  Suivez l'évolution de votre énergie au fil du temps
                </p>
              </div>
              <EnergyTrends assessments={assessments} />
            </Card>
          </motion.div>
        )}

        {/* Section 5: Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6"
        >
          <Button
            onClick={() => onNavigate('diagnosis')}
            className="p-6 h-auto flex flex-col items-center gap-4 bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Brain className="w-8 h-8" />
            <div className="text-lg font-semibold">Nouveau diagnostic</div>
            <div className="text-sm opacity-80">Évaluez votre état énergétique actuel</div>
          </Button>

          <Button
            variant="outline"
            onClick={() => onNavigate('history')}
            className="p-6 h-auto flex flex-col items-center gap-4"
          >
            <History className="w-8 h-8" />
            <div className="text-lg font-semibold">Voir l'historique</div>
            <div className="text-sm opacity-80">Consultez vos diagnostics précédents</div>
          </Button>
        </motion.div>
      </div>
    </div>
  );
}